"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import Botcheck from "@/components/forms/Botcheck";
import MarketingConsent from "@/components/forms/MarketingConsent";
import { isBotSubmission } from "@/lib/botcheck";
import { trackDownload, trackEvent } from "@/lib/analytics";
import {
  submitGuideRequest,
  whatsappClaudeGuide,
  type DownloadGuide,
} from "@/lib/gated-guide-download";

const inputClass =
  "mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base text-neutral-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

function downloadGuide(guide: DownloadGuide) {
  const link = document.createElement("a");
  link.href = guide.pdfPath;
  link.download = guide.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Reuses the plan-report lead-capture pattern; marketing is an optional opt-in. */
export function GatedGuideDownload({ guide }: { guide: DownloadGuide }) {
  const [status, setStatus] = useState<"idle" | "sending" | "ready" | "error">(
    "idle",
  );
  const [marketingConsent, setMarketingConsent] = useState(false);
  const sending = useRef(false);
  const confirmation = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === "ready") confirmation.current?.focus();
  }, [status]);

  function startDownload() {
    try {
      trackDownload(guide.filename, "pdf", {
        gated: true,
        guide_id: guide.id,
        source_path: guide.sourcePath,
      });
    } catch {
      // Analytics must not block access to an accepted download.
    }
    downloadGuide(guide);
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending.current || status === "ready") return;
    if (isBotSubmission(event.currentTarget)) {
      setStatus("error");
      return;
    }
    const fields = new FormData(event.currentTarget);
    sending.current = true;
    setStatus("sending");
    try {
      await submitGuideRequest(
        {
          name: String(fields.get("name") ?? ""),
          email: String(fields.get("email") ?? ""),
          company: String(fields.get("company") ?? ""),
          marketingConsent,
        },
        guide,
        process.env.NEXT_PUBLIC_WEB3FORMS_KEY,
      );
    } catch {
      setStatus("error");
      sending.current = false;
      return;
    }
    try {
      trackEvent("guide_download_request", {
        guide_id: guide.id,
        source_path: guide.sourcePath,
        category: "lead",
      });
    } catch {
      // Do not turn an accepted lead into a form error if analytics fails.
    }
    setStatus("ready");
    sending.current = false;
    try {
      startDownload();
    } catch {
      // The ready state keeps a manual download button if the browser blocks it.
    }
  }

  return (
    <section
      id="download-setup-guide"
      aria-labelledby="whatsapp-guide-heading"
      className="not-prose my-10 scroll-mt-28 overflow-hidden rounded-2xl border border-primary/25 bg-card"
    >
      <div className="grid md:grid-cols-[0.7fr_1.3fr]">
        <div className="border-b border-border bg-neutral-surface/60 p-6 md:border-b-0 md:border-r sm:p-8">
          <div className="grid grid-cols-[96px_1fr] items-center gap-x-4 md:block">
            <p className="col-start-2 self-end text-xs font-semibold uppercase tracking-[0.16em] text-primary md:mb-4">
              Free Anchor Sprint guide
            </p>
            <Image
              src={guide.coverImage}
              alt="Cover of Anchor Sprint's WhatsApp Business API, Claude and Composio setup guide"
              width={595}
              height={842}
              sizes="(max-width: 767px) 96px, 280px"
              className="col-start-1 row-span-2 row-start-1 mx-auto h-auto w-full max-w-[96px] rounded-lg border border-border shadow-sm md:max-w-[240px]"
            />
            <p className="col-start-2 mt-2 self-start text-xs leading-relaxed text-neutral-muted md:mt-4 md:text-center">
              {guide.pages} pages · English PDF
              <br />
              Research checked {guide.checkedAt}
            </p>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <h2
            id="whatsapp-guide-heading"
            className="font-display text-2xl font-semibold leading-tight text-neutral-emphasis"
          >
            {guide.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-muted">
            {guide.description}
          </p>
          {status === "ready" ? (
            <div
              ref={confirmation}
              tabIndex={-1}
              role="status"
              className="mt-6 rounded-xl border border-primary/25 bg-primary/5 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <CheckCircle2
                className="h-8 w-8 text-primary"
                aria-hidden="true"
              />
              <h3 className="mt-3 font-display text-xl font-semibold">
                Your guide is ready.
              </h3>
              <p className="mt-2 text-sm text-neutral-muted">
                The download should start now. Use the button below if it does
                not start.
              </p>
              <button
                type="button"
                onClick={startDownload}
                className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download the PDF
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              aria-label="Download the WhatsApp and Claude setup guide"
              className="mt-6"
            >
              <Botcheck />
              <fieldset
                disabled={status === "sending"}
                className="space-y-4 disabled:opacity-70"
              >
                <legend className="sr-only">
                  Your details for this download
                </legend>
                <label
                  htmlFor="whatsapp-guide-name"
                  className="block text-sm font-medium"
                >
                  Name (required)
                  <input
                    id="whatsapp-guide-name"
                    name="name"
                    autoComplete="name"
                    required
                    maxLength={120}
                    className={inputClass}
                  />
                </label>
                <label
                  htmlFor="whatsapp-guide-email"
                  className="block text-sm font-medium"
                >
                  Email (required)
                  <input
                    id="whatsapp-guide-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    className={inputClass}
                  />
                </label>
                <label
                  htmlFor="whatsapp-guide-company"
                  className="block text-sm font-medium"
                >
                  Company (optional)
                  <input
                    id="whatsapp-guide-company"
                    name="company"
                    autoComplete="organization"
                    maxLength={160}
                    className={inputClass}
                  />
                </label>
                <MarketingConsent
                  id="whatsapp-guide-marketing"
                  checked={marketingConsent}
                  onChange={setMarketingConsent}
                  disabled={status === "sending"}
                />
                <p className="text-xs leading-relaxed text-neutral-muted">
                  Marketing consent is optional. We use your details to process
                  this download. We send marketing emails only if you choose to
                  receive them.{" "}
                  <Link
                    href="/privacy-policy/"
                    className="text-primary underline underline-offset-2"
                  >
                    Read our Privacy Notice
                  </Link>
                  .
                </p>
                {status === "error" && (
                  <p role="alert" className="text-sm text-destructive">
                    We could not process your request. Please try again or{" "}
                    <Link href="/contact/" className="underline">
                      contact us
                    </Link>
                    .
                  </p>
                )}
                <button
                  type="submit"
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-primary-foreground hover:bg-primary-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4" aria-hidden="true" />
                      Processing your request…
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Get the free setup guide
                    </>
                  )}
                </button>
              </fieldset>
            </form>
          )}
          <p className="mt-5 text-xs leading-relaxed text-neutral-muted">
            For business messaging accounts, including solo operators testing a
            business workflow. Personal WhatsApp accounts are not supported by
            this API connection.
          </p>
        </div>
      </div>
    </section>
  );
}

export function WhatsAppClaudeGuideDownload() {
  return <GatedGuideDownload guide={whatsappClaudeGuide} />;
}
